/**
 * DDS Predictive Search
 * Autocomplete cu produse, suggestii, imagine, preț — desktop + mobile
 * Vanilla JS, fără dependențe externe
 */
(function () {
  'use strict';

  /* ── Constante ── */
  var DEBOUNCE_MS = 220;
  var MIN_CHARS = 2;
  var MAX_PRODUCTS = 5;
  var MAX_QUERIES = 4;
  var CACHE = {};
  var ABORT_CONTROLLERS = {};

  /* ── Normalize diacritice pentru search inteligent local ── */
  function normDiacritics(str) {
    return str
      .toLowerCase()
      .replace(/ă|â/g, 'a')
      .replace(/î/g, 'i')
      .replace(/ș|ş/g, 's')
      .replace(/ț|ţ/g, 't')
      .replace(/é|è|ê/g, 'e')
      .replace(/ó|ò|ô/g, 'o')
      .replace(/ú|ù|û/g, 'u');
  }

  /* ── Highlight termen căutat în text ── */
  function highlight(text, term) {
    if (!term) return text;
    var escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var rx = new RegExp('(' + escaped + ')', 'gi');
    return text.replace(rx, '<mark>$1</mark>');
  }

  /* ── Formatare preț RON ── */
  function formatPrice(amount) {
    // Shopify /search/suggest.json returnează prețul deja în Lei (nu în bană),
    // ca număr zecimal (ex: 59.0, 129.99)
    var lei = parseFloat(amount);
    if (isNaN(lei)) return '';
    return lei.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Lei';
  }

  /* ── Debounce ── */
  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* ── Fetch results de la Shopify Predictive Search API ── */
  function fetchResults(query, instanceId, callback) {
    var cacheKey = query.toLowerCase();
    if (CACHE[cacheKey]) { callback(null, CACHE[cacheKey]); return; }

    // Anulăm request-ul anterior pentru această instanță
    if (ABORT_CONTROLLERS[instanceId]) {
      ABORT_CONTROLLERS[instanceId].abort();
    }
    var ctrl = new AbortController();
    ABORT_CONTROLLERS[instanceId] = ctrl;

    var apiUrl = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product,query,collection'
      + '&resources[limit]=' + MAX_PRODUCTS
      + '&resources[options][unavailable_products]=hide'
      + '&resources[options][fields]=title,product_type,variants.title,vendor,tag,body';

    fetch(apiUrl, {
      signal: ctrl.signal,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        CACHE[cacheKey] = data.resources;
        callback(null, data.resources);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        callback(err, null);
      });
  }

  /* ── Build HTML pentru dropdown ── */
  function buildDropdownHTML(resources, query) {
    var html = '';
    var products = (resources.results && resources.results.products) || [];
    var queries = (resources.results && resources.results.queries) || [];
    var collections = (resources.results && resources.results.collections) || [];

    var hasContent = products.length > 0 || queries.length > 0 || collections.length > 0;
    if (!hasContent) {
      html = '<div class="dds-ps__empty">'
        + '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>'
        + '<p>Niciun rezultat pentru <strong>"' + query + '"</strong></p>'
        + '<p class="dds-ps__empty-hint">Încearcă: articulații, detox, imunitate, vitamina D</p>'
        + '</div>';
      return html;
    }

    /* Sugestii de căutare + colecții */
    var suggestions = queries.slice(0, MAX_QUERIES).concat(collections.slice(0, 2));
    if (suggestions.length > 0) {
      html += '<div class="dds-ps__section dds-ps__suggestions">';
      html += '<div class="dds-ps__section-label">Sugestii</div>';
      html += '<ul class="dds-ps__list" role="listbox">';
      suggestions.forEach(function (item) {
        var title = item.title || item.styled_text || '';
        var url = item.url || '/search?q=' + encodeURIComponent(item.handle || title);
        var badge = item.__typename === 'Collection' ? '<span class="dds-ps__badge">Categorie</span>' : '';
        html += '<li class="dds-ps__item dds-ps__item--query" role="option">'
          + '<a href="' + url + '" class="dds-ps__link-query">'
          + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>'
          + '<span>' + highlight(title, query) + '</span>'
          + badge
          + '</a>'
          + '</li>';
      });
      html += '</ul></div>';
    }

    /* Produse */
    if (products.length > 0) {
      html += '<div class="dds-ps__section dds-ps__products">';
      html += '<div class="dds-ps__section-label">Produse</div>';
      html += '<ul class="dds-ps__list" role="listbox">';

      products.forEach(function (product) {
        var imgUrl = product.featured_image && product.featured_image.url
          ? product.featured_image.url.replace(/(\.(png|jpg|jpeg|webp))(\?.*)?$/, '_150x150$1')
          : null;

        /* ── BUGFIX: parseFloat explicit — Shopify returnează strings, nu numere
           String comparison: "122.80" > "79.82" → FALSE ("1" < "7" lexicografic)
           Numeric comparison: 122.80 > 79.82 → TRUE ── */
        var priceNum = parseFloat(product.price) || 0;
        var compareNum = parseFloat(product.compare_at_price_max) || 0;

        var price = priceNum > 0 ? formatPrice(priceNum) : '';
        var comparePrice = compareNum > priceNum ? formatPrice(compareNum) : null;
        var discount = '';
        if (comparePrice && compareNum > priceNum) {
          var pct = Math.round((1 - priceNum / compareNum) * 100);
          discount = '<span class="dds-ps__badge dds-ps__badge--sale">-' + pct + '%</span>';
        }
        var imgHtml = imgUrl
          ? '<img class="dds-ps__product-img" src="' + imgUrl + '" alt="' + product.title + '" width="60" height="60" loading="lazy">'
          : '<div class="dds-ps__product-img-placeholder"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></div>';

        html += '<li class="dds-ps__item dds-ps__item--product" role="option">'
          + '<a href="' + product.url + '" class="dds-ps__link-product">'
          + '<div class="dds-ps__product-thumb">' + imgHtml + '</div>'
          + '<div class="dds-ps__product-info">'
          + '<p class="dds-ps__product-title">' + highlight(product.title, query) + '</p>'
          + '<div class="dds-ps__product-price-row">'
          + '<span class="dds-ps__product-price">' + price + '</span>'
          + (comparePrice ? '<span class="dds-ps__product-compare">' + comparePrice + '</span>' : '')
          + discount
          + '</div>'
          + '</div>'
          + '</a>'
          + '</li>';
      });

      html += '</ul>';
      html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="dds-ps__see-all">Toate rezultatele pentru "' + query + '" →</a>';
      html += '</div>';
    } else {
      html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="dds-ps__see-all dds-ps__see-all--solo">Caută "' + query + '" →</a>';
    }

    return html;
  }

  /* ── DDS Search Instance ── */
  function DDSSearch(inputEl, dropdownEl, instanceId) {
    var self = this;
    self.input = inputEl;
    self.dropdown = dropdownEl;
    self.instanceId = instanceId;
    self.isOpen = false;
    self.activeIndex = -1;
    self.items = [];

    /* Init */
    self._debouncedSearch = debounce(function (q) { self.doSearch(q); }, DEBOUNCE_MS);

    self.input.addEventListener('input', function () {
      var q = self.input.value.trim();
      if (q.length < MIN_CHARS) { self.close(); return; }
      self.setLoading(true);
      self._debouncedSearch(q);
    });

    self.input.addEventListener('focus', function () {
      var q = self.input.value.trim();
      if (q.length >= MIN_CHARS && !self.isOpen) {
        var cacheKey = q.toLowerCase();
        if (CACHE[cacheKey]) {
          self.render(CACHE[cacheKey], q);
        } else {
          self.doSearch(q);
        }
      }
    });

    /* Keyboard navigation */
    self.input.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        self.moveUp(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        self.moveUp(-1);
      } else if (e.key === 'Enter') {
        var active = self.dropdown.querySelector('.dds-ps__item.keyboard-active a');
        if (active) { e.preventDefault(); active.click(); }
      } else if (e.key === 'Escape') {
        self.close();
      }
    });

    /* Click outside */
    document.addEventListener('click', function (e) {
      if (!self.dropdown.contains(e.target) && e.target !== self.input) {
        self.close();
      }
    });

    /* Touch outside */
    document.addEventListener('touchend', function (e) {
      if (!self.dropdown.contains(e.target) && e.target !== self.input) {
        self.close();
      }
    }, { passive: true });
  }

  DDSSearch.prototype.doSearch = function (q) {
    var self = this;
    fetchResults(q, self.instanceId, function (err, resources) {
      self.setLoading(false);
      if (err || !resources) return;
      self.render(resources, q);
    });
  };

  DDSSearch.prototype.render = function (resources, query) {
    var self = this;
    var html = buildDropdownHTML(resources, query);
    self.dropdown.innerHTML = html;
    self.open();
    self.items = Array.from(self.dropdown.querySelectorAll('.dds-ps__item'));
    self.activeIndex = -1;
  };

  DDSSearch.prototype.moveUp = function (dir) {
    var self = this;
    if (!self.items.length) return;
    self.items.forEach(function (el) { el.classList.remove('keyboard-active'); });
    self.activeIndex = (self.activeIndex + dir + self.items.length) % self.items.length;
    self.items[self.activeIndex].classList.add('keyboard-active');
    self.items[self.activeIndex].scrollIntoView({ block: 'nearest' });
  };

  DDSSearch.prototype.open = function () {
    var self = this;
    self.dropdown.classList.add('open');
    self.dropdown.setAttribute('aria-hidden', 'false');
    self.isOpen = true;
  };

  DDSSearch.prototype.close = function () {
    var self = this;
    self.dropdown.classList.remove('open');
    self.dropdown.setAttribute('aria-hidden', 'true');
    self.isOpen = false;
    self.activeIndex = -1;
  };

  DDSSearch.prototype.setLoading = function (on) {
    var self = this;
    if (on) {
      self.dropdown.innerHTML = '<div class="dds-ps__loading"><span class="dds-ps__spinner"></span><span>Se caută...</span></div>';
      self.open();
    }
  };

  /* ── Init: atașăm la toate câmpurile de search DDS ── */
  function initAll() {
    var pairs = [
      { input: 'dds-search-desktop', dropdown: 'dds-ps-dropdown-desktop', id: 'desktop' },
      { input: 'dds-search-drawer', dropdown: 'dds-ps-dropdown-drawer', id: 'drawer' },
      { input: 'dds-bm-search-input', dropdown: 'dds-ps-dropdown-mobile', id: 'mobile' }
    ];

    pairs.forEach(function (pair) {
      var input = document.getElementById(pair.input);
      var dropdown = document.getElementById(pair.dropdown);
      if (input && dropdown) {
        new DDSSearch(input, dropdown, pair.id);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();
